import React from 'react'
import { WhiteBox, ButtonSection } from '@/components/Layout'
import { ValidationBoundary, ValidationState, Validator } from '@/components/validation'
import {
  Form,
  FormGroup,
  Label,
  Row,
  Col
} from 'reactstrap'
import Icon from '@/components/Icon'
import { Button } from '@/components/button'
import CheckBox from '@/components/CheckBox'
import LoadingIndicator from '@/components/LoadingIndicator'
import { NumericInput } from '@/components/inputs'
import ErrorBoundary from '@/components/ErrorBoundary'
import { openTwoFactorDialog } from '@/utils/twoFactorHelper'

import Resx from '@/components/Resx'

const PasswordPolicyForm = (props) => {
  const [minimalPasswordLength, setMinimalPasswordLength] = React.useState(props.minimalPasswordLength)
  const [isLowerCaseCharRequired, setIsLowerCaseCharRequired] = React.useState(props.isLowerCaseCharRequired)
  const [isUpperCaseCharRequired, setIsUpperCaseCharRequired] = React.useState(props.isUpperCaseCharRequired)
  const [isNumberCharRequired, setIsNumberCharRequired] = React.useState(props.isNumberCharRequired)
  const [isNonLetterOrDigitCharRequired, setIsNonLetterOrDigitCharRequired] = React.useState(props.isNonLetterOrDigitCharRequired)
  const [isTwoFactorAuthenticationRequired, setIsTwoFactorAuthenticationRequired] = React.useState(props.isTwoFactorAuthenticationRequired)
  const [lockingAccountNumberOfFailAttempts, setLockingAccountNumberOfFailAttempts] = React.useState(props.lockingAccountNumberOfFailAttempts)
  const [lockDurationInMinutes, setLockDurationInMinutes] = React.useState(props.lockDurationInMinutes)
  const [numberOfDifferentPasswordsInHistory, setNumberOfDifferentPasswordsInHistory] = React.useState(props.numberOfDifferentPasswordsInHistory)
  const [passwordValidityInDays, setPasswordValidityInDays] = React.useState(props.passwordValidityInDays)

  const { localize, fetching, updating } = props
  if (fetching || updating) { return <LoadingIndicator /> }

  const doSave = () => {
    props.save({
      minimalPasswordLength,
      isLowerCaseCharRequired,
      isUpperCaseCharRequired,
      isNumberCharRequired,
      isNonLetterOrDigitCharRequired,
      lockingAccountNumberOfFailAttempts,
      lockDurationInMinutes,
      numberOfDifferentPasswordsInHistory,
      passwordValidityInDays,
      isTwoFactorAuthenticationRequired
    })
  }
  const handleSave = () => {
    if (props.originalIsTwoFactorAuthenticationRequired && !isTwoFactorAuthenticationRequired) {
      openTwoFactorDialog(() => doSave())
    } else {
      doSave()
    }
  }
  const handleForceChangePassword = () => {
    props.forceUsersToChangePasswordAtNextLogon()
  }
  return (
    <ErrorBoundary>
      <ValidationBoundary>
        <Form>
          <Row>
            <Col sm='6'>
              <FormGroup>
                <Label style={{ display: 'block' }}><Resx name='Security.MinimalPasswordLength' /></Label>
                <Validator name='minimalPasswordLength' value={minimalPasswordLength} required min={1} max={30}>
                  <NumericInput integerOnly value={minimalPasswordLength} onChange={value => setMinimalPasswordLength(value)} />
                </Validator>
              </FormGroup>
              <FormGroup>
                <Label style={{ display: 'block' }}><Resx name='Security.WrongAttemptsToLockAccount' /></Label>
                <Validator name='lockingAccountNumberOfFailAttempts' value={lockingAccountNumberOfFailAttempts} required min={1}>
                  <NumericInput integerOnly value={lockingAccountNumberOfFailAttempts} onChange={value => setLockingAccountNumberOfFailAttempts(value)} />
                </Validator>
              </FormGroup>
              <FormGroup>
                <Label for='lockDurationInMinutes' style={{ display: 'block' }}><Resx name='Security.LockLengthInMins' /></Label>
                <Validator name='lockDurationInMinutes' value={lockDurationInMinutes} required min={1}>
                  <NumericInput integerOnly value={lockDurationInMinutes} onChange={value => setLockDurationInMinutes(value)} />
                </Validator>
              </FormGroup>
              <FormGroup>
                <Label style={{ display: 'block' }}><Resx name='Security.NumberOfDifferentPasswords' /></Label>
                <Validator name='numberOfDifferentPasswordsInHistory' value={numberOfDifferentPasswordsInHistory} required min={0}>
                  <NumericInput integerOnly value={numberOfDifferentPasswordsInHistory} onChange={value => setNumberOfDifferentPasswordsInHistory(value)} />
                </Validator>
              </FormGroup>
              <FormGroup>
                <Label style={{ display: 'block' }}><Resx name='Security.PasswordValidityDays' /></Label>
                <Validator name='passwordValidityInDays' value={passwordValidityInDays} required min={1} max={10000}>
                  <NumericInput integerOnly value={passwordValidityInDays} onChange={value => setPasswordValidityInDays(value)} />
                </Validator>
              </FormGroup>
            </Col>
            <Col sm='6'>
              <FormGroup className='mt-3 ml-3'>
                <CheckBox id='isLowerCaseCharRequired' checked={isLowerCaseCharRequired} onChange={value => setIsLowerCaseCharRequired(value)} label={localize('Security.RequiredLowerCaseChars')} />
                <CheckBox id='isUpperCaseCharRequired' checked={isUpperCaseCharRequired} onChange={value => setIsUpperCaseCharRequired(value)} label={localize('Security.RequireUpperCaseChars')} />
                <CheckBox id='isNumberCharRequired' checked={isNumberCharRequired} onChange={value => setIsNumberCharRequired(value)} label={localize('Security.RequireNumbers')} />
                <CheckBox id='isNonLetterOrDigitCharRequired' checked={isNonLetterOrDigitCharRequired} onChange={value => setIsNonLetterOrDigitCharRequired(value)} label={localize('Security.RequireSpecialChars')} />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm='6'>
              <FormGroup>
                <CheckBox id='isTwoFactorAuthenticationRequired' checked={isTwoFactorAuthenticationRequired} onChange={value => setIsTwoFactorAuthenticationRequired(value)} label={localize('Security.RequireTwoFactorAuthentication')} />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <WhiteBox>
              <Button color='danger' disabled={props.forcingPasswordChange} confirmText='Security.ConfirmForcePasswordChange' onClick={handleForceChangePassword}><Icon name='key' /> <span><Resx name='Security.EnforcePasswordPolicy' /></span></Button>
            </WhiteBox>
          </Row>
          <Row>
            <Col sm='12'>
              <ButtonSection>
                <div className='ml-auto'>
                  <ValidationState render={({ isValid, serverError }) =>
                    <Button id='save-button' color='success' disabled={!isValid} onClick={handleSave} >
                      <Icon name='save' />
                      <span>
                        <Resx name='Common.Save' />
                      </span>
                    </Button>
                  } />
                </div>
              </ButtonSection>
            </Col>
          </Row>
        </Form>
      </ValidationBoundary>
    </ErrorBoundary>
  )
}

export default PasswordPolicyForm



// WEBPACK FOOTER //
// ./src/components/administration/passwordPolicy/index.js